import { useState, useEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';

// Mui Components
import { IconButton, TextareaAutosize } from '@mui/material';

// Custom Components
import CustomButton from '../CustomButton';

// Prime-React Components
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Timeline } from 'primereact/timeline';
import FormikMultiSelect from '../form/FormikMultiSelect';
import CompanyAutocomplete from '../form/CompanyAutocomplete';

// Constants
import {
  APLHABET_PATTERN_REGEX,
  BUTTON_TYPES,
  CALLSTATUS_MAP,
  CANDIDATE_LOCATIONS,
  CANDIDATE_SOURCE_TYPE,
  LABEL_TYPES,
  MOBILE_NUMBER_REGEX,
  ROLES
} from '../../constants/common';
import { CALL_STATUS_OPTIONS } from '../../constants/dropdownOptions';

// Utils
import { renderHrAvatar } from '../../utils/common';

// Redux & APIs
import {
  addCandidateCallStatusAction,
  editCandidate,
  editCandidateScreeningNoteAction
} from '../../redux/actions/candidates';
import { candidateDetailsAction } from '../../redux/actions/userDetails';
import { getInterviewTechnology } from '../../redux/actions/hr';

const GENDER_OPTIONS = [
  { name: 'Male', code: 1 },
  { name: 'Female', code: 2 },
  { name: 'Other', code: 3 }
];

const EDITABLE_FIELDS = [
  'Name',
  'Gender',
  'Experience',
  'Email Address',
  'Mobile',
  'Second Mobile',
  'Current Company',
  'Technology',
  'Current CTC',
  'Candidate Location',
  'Candidate Source',
  'Employee Ref Name'
];

const DetailCard = ({
  items,
  isEditing,
  inlineForm,
  setInlineForm,
  fieldErrors,
  interviewTechnologyType
}) => {
  const renderInlineInput = (title) => {
    switch (title) {
      case 'Name':
        return (
          <div className="ud-dual-inputs">
            <InputText
              value={inlineForm.firstName}
              onChange={(e) => setInlineForm({ ...inlineForm, firstName: e.target.value })}
              placeholder="First Name"
              className="ud-inline-input flex-1"
            />
            <InputText
              value={inlineForm.lastName}
              onChange={(e) => setInlineForm({ ...inlineForm, lastName: e.target.value })}
              placeholder="Last Name"
              className="ud-inline-input flex-1"
            />
          </div>
        );

      case 'Gender':
        return (
          <Dropdown
            value={inlineForm.gender}
            options={GENDER_OPTIONS}
            onChange={(e) => setInlineForm({ ...inlineForm, gender: e.value })}
            optionLabel="name"
            optionValue="code"
            placeholder="Select Gender"
            className="ud-inline-dropdown w-full"
            panelClassName="custom-dropdown-panel"
          />
        );

      case 'Experience':
        return (
          <div className="ud-dual-inputs">
            <div className="flex-1">
              <InputNumber
                value={inlineForm.yearsOfExperience}
                onValueChange={(e) => setInlineForm({ ...inlineForm, yearsOfExperience: e.value })}
                placeholder="Years"
                min={0}
                max={100}
                className="w-full"
                inputClassName="ud-inline-input w-full"
              />
            </div>
            <div className="flex-1">
              <InputNumber
                value={inlineForm.monthsOfExperience}
                onValueChange={(e) => setInlineForm({ ...inlineForm, monthsOfExperience: e.value })}
                placeholder="Months"
                min={0}
                max={11}
                className="w-full"
                inputClassName="ud-inline-input w-full"
              />
            </div>
          </div>
        );

      case 'Email Address':
        return (
          <InputText
            value={inlineForm.email}
            onChange={(e) => setInlineForm({ ...inlineForm, email: e.target.value })}
            placeholder="Email Address"
            className="ud-inline-input w-full"
          />
        );

      case 'Mobile':
        return (
          <InputText
            value={inlineForm.mobile}
            onChange={(e) => setInlineForm({ ...inlineForm, mobile: e.target.value })}
            placeholder="Mobile Number"
            maxLength={10}
            className="ud-inline-input w-full"
          />
        );

      case 'Second Mobile':
        return (
          <InputText
            value={inlineForm.secondaryMobile}
            onChange={(e) => setInlineForm({ ...inlineForm, secondaryMobile: e.target.value })}
            placeholder="Secondary Mobile"
            maxLength={10}
            className="ud-inline-input w-full"
          />
        );

      case 'Current Company':
        return (
          <CompanyAutocomplete
            value={inlineForm.currentCompany}
            onChange={(e) => {
              const val =
                typeof e.value === 'string' ? e.value : e.value?.name || e.target?.value || '';
              setInlineForm({ ...inlineForm, currentCompany: val });
            }}
            placeholder="Current Company"
            className="w-full"
            inputClassName="ud-inline-input w-full"
          />
        );

      case 'Technology':
        return (
          <FormikMultiSelect
            formik={{
              values: inlineForm,
              touched: {},
              errors: fieldErrors,
              handleBlur: () => {},
              setFieldValue: (name, val) => setInlineForm((prev) => ({ ...prev, [name]: val }))
            }}
            name="technology"
            options={interviewTechnologyType || []}
            optionLabel="Technology"
            optionValue="ID"
            placeholder="Select Technology *"
            className="ud-inline-dropdown w-full"
            panelClassName="custom-dropdown-panel"
            display="chip"
            maxSelectedLabels={3}
            selectedItemsLabel="{0} items selected"
            filter
          />
        );

      case 'Current CTC':
        return (
          <InputNumber
            value={inlineForm.currentCTC}
            onValueChange={(e) => setInlineForm({ ...inlineForm, currentCTC: e.value })}
            placeholder="Current CTC"
            mode="decimal"
            min={0}
            max={99}
            maxFractionDigits={2}
            className="w-full"
            inputClassName="ud-inline-input w-full"
          />
        );

      case 'Candidate Location':
      case 'Location':
        return (
          <div className="flex flex-column gap-2 w-full">
            <Dropdown
              value={inlineForm.candidateLocation}
              options={CANDIDATE_LOCATIONS}
              onChange={(e) => setInlineForm({ ...inlineForm, candidateLocation: e.value })}
              optionLabel="name"
              optionValue="code"
              placeholder="Select Location"
              className="ud-inline-dropdown w-full"
              panelClassName="custom-dropdown-panel"
            />
            {inlineForm.candidateLocation === 'other' && (
              <InputText
                value={inlineForm.location}
                onChange={(e) => setInlineForm({ ...inlineForm, location: e.target.value })}
                placeholder="Enter Location"
                className="ud-inline-input w-full mt-2"
              />
            )}
          </div>
        );

      case 'Candidate Source':
        return (
          <div className="flex flex-column gap-2 w-full">
            <Dropdown
              value={inlineForm.candidateSource}
              options={CANDIDATE_SOURCE_TYPE}
              onChange={(e) => setInlineForm({ ...inlineForm, candidateSource: e.value })}
              optionLabel="name"
              optionValue="code"
              placeholder="Select Source"
              className="ud-inline-dropdown w-full"
              panelClassName="custom-dropdown-panel"
            />
            {inlineForm.candidateSource === 4 && (
              <InputText
                value={inlineForm.referenceEmployeeName}
                onChange={(e) =>
                  setInlineForm({ ...inlineForm, referenceEmployeeName: e.target.value })
                }
                placeholder="Employee Ref Name"
                className="ud-inline-input w-full mt-2"
              />
            )}
          </div>
        );

      case 'Employee Ref Name':
        return (
          <InputText
            value={inlineForm.referenceEmployeeName}
            onChange={(e) =>
              setInlineForm({ ...inlineForm, referenceEmployeeName: e.target.value })
            }
            placeholder="Employee Ref Name"
            className="ud-inline-input w-full"
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="ud-details-grid">
      {items?.map((item, i) => {
        const isEditableField = isEditing && EDITABLE_FIELDS.includes(item.title);
        const err = fieldErrors[item.title];

        return (
          <div
            className={`ud-detail-item ${isEditableField ? 'ud-detail-item-editing' : ''}`}
            key={i}>
            <span className="ud-detail-label">{item.title}</span>
            {isEditableField ? (
              <div className="ud-inline-edit-wrap">
                <div className="ud-inline-input-container">
                  {renderInlineInput(item.title)}
                  {err && <span className="ud-field-error-msg">{err}</span>}
                </div>
              </div>
            ) : (
              <div className="ud-detail-value-wrap">
                <span className="ud-detail-value">{item.value || '--'}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const UserDetails = ({
  data,
  header,
  onHide,
  show,
  callStatusHistory,
  candidateId,
  readOnly = false,
  onStatusUpdate
}) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const userDetails = useSelector((state) => state.userDetails);
  const candidateDetailsState = useSelector((state) => state.candidates?.candidateDetails);
  const { interviewTechnologyType } = useSelector((state) => state.hrs);

  const isReadOnly = readOnly || auth?.userType === ROLES.ADMIN;

  const historyList =
    callStatusHistory && callStatusHistory.length > 0
      ? callStatusHistory
      : userDetails?.CallStatusHistory || [];

  const [showHistory, setShowHistory] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCallStatus, setNewCallStatus] = useState(null);
  const [newScreeningNote, setNewScreeningNote] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingNoteStatus, setSavingNoteStatus] = useState(false);

  const [editingHistoryIdx, setEditingHistoryIdx] = useState(null);
  const [editingNoteValue, setEditingNoteValue] = useState('');

  // Global inline edit state
  const [isEditing, setIsEditing] = useState(false);
  const [savingForm, setSavingForm] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [inlineForm, setInlineForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    secondaryMobile: '',
    gender: 1,
    yearsOfExperience: 0,
    monthsOfExperience: 0,
    currentCompany: '',
    currentCTC: null,
    candidateLocation: '',
    location: '',
    candidateSource: 1,
    referenceEmployeeName: '',
    technology: []
  });

  useEffect(() => {
    if (show) {
      dispatch(getInterviewTechnology());
    } else {
      setIsEditing(false);
      setFieldErrors({});
    }
  }, [dispatch, show]);

  const getRawCandidateData = () => {
    if (userDetails?.ID && String(userDetails.ID) === String(candidateId)) {
      return userDetails;
    }
    if (candidateDetailsState?.ID && String(candidateDetailsState.ID) === String(candidateId)) {
      return candidateDetailsState;
    }
    return userDetails?.ID ? userDetails : candidateDetailsState || {};
  };

  const populateInlineForm = () => {
    const raw = getRawCandidateData();
    let techIds = [];
    if (raw?.CandidateTechnology && raw.CandidateTechnology.length > 0) {
      techIds = raw.CandidateTechnology.map((t) => {
        if (typeof t === 'object' && t !== null) {
          return t.ID ?? t.MapT?.ID ?? t.TechnologyID ?? t.value ?? t.id;
        }
        return t;
      }).filter((v) => v !== null && v !== undefined);
    } else if (raw?.UMap && raw.UMap.length > 0) {
      techIds = raw.UMap.map((u) => u.MapT?.ID ?? u.TechnologyID ?? u.ID).filter(
        (v) => v !== null && v !== undefined
      );
    }

    if (interviewTechnologyType?.length > 0 && techIds.length > 0) {
      const sampleOptionId = interviewTechnologyType[0]?.ID;
      if (typeof sampleOptionId === 'number') {
        techIds = techIds.map((id) => Number(id)).filter((id) => !isNaN(id));
      } else if (typeof sampleOptionId === 'string') {
        techIds = techIds.map((id) => String(id));
      }
    }

    let isLocationFromDropdown = CANDIDATE_LOCATIONS.some(
      (location) => location.code === raw?.Address
    );

    return {
      firstName: raw?.FirstName || '',
      lastName: raw?.LastName || '',
      email: raw?.Email || '',
      mobile: raw?.Mobile === 'null' || !raw?.Mobile ? '' : String(raw.Mobile),
      secondaryMobile: raw?.SecondaryMobile || '',
      gender: raw?.Gender ? Number(raw.Gender) : 1,
      yearsOfExperience: raw?.YearsOfExperience ?? 0,
      monthsOfExperience: raw?.MonthsOfExperience ?? 0,
      currentCompany: raw?.CurrentCompany || '',
      currentCTC: raw?.CurrentCTC ?? null,
      candidateLocation: isLocationFromDropdown ? raw?.Address : raw?.Address ? 'other' : '',
      location: isLocationFromDropdown ? '' : raw?.Address || '',
      candidateSource: raw?.CandidateSource ? Number(raw.CandidateSource) : 1,
      referenceEmployeeName: raw?.EmployeeReferenceName || '',
      technology: techIds
    };
  };

  const handleToggleEditMode = () => {
    if (isEditing) {
      setIsEditing(false);
      setFieldErrors({});
    } else {
      setInlineForm(populateInlineForm());
      setFieldErrors({});
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setFieldErrors({});
  };

  const validateAllFields = (form) => {
    const errors = {};
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

    // Name
    if (!form.firstName?.trim()) {
      errors['Name'] = 'First Name is required.';
    } else if (form.firstName.trim().length < 2) {
      errors['Name'] = 'Must be at least 2 characters';
    } else if (form.firstName.trim().length > 15) {
      errors['Name'] = 'Must be 15 characters or less';
    } else if (!APLHABET_PATTERN_REGEX.test(form.firstName.trim())) {
      errors['Name'] = 'First Name should only contain characters';
    } else if (!form.lastName?.trim()) {
      errors['Name'] = 'Last Name is required.';
    } else if (form.lastName.trim().length < 2) {
      errors['Name'] = 'Must be at least 2 characters';
    } else if (form.lastName.trim().length > 20) {
      errors['Name'] = 'Must be 20 characters or less';
    } else if (!APLHABET_PATTERN_REGEX.test(form.lastName.trim())) {
      errors['Name'] = 'Last Name should only contain characters';
    }

    // Gender
    if (!form.gender) {
      errors['Gender'] = 'Gender is required.';
    }

    // Email
    if (!form.email?.trim()) {
      errors['Email Address'] = 'Email Address is required.';
    } else if (!emailRegex.test(form.email.trim())) {
      errors['Email Address'] = 'Enter a valid email address';
    }

    // Mobile
    if (!form.mobile?.trim()) {
      errors['Mobile'] = 'Mobile Number is required.';
    } else if (!MOBILE_NUMBER_REGEX.test(form.mobile.trim())) {
      errors['Mobile'] = 'Mobile Number Must Be 10 Digits Only.';
    }

    // Second Mobile
    if (form.secondaryMobile?.trim()) {
      if (!MOBILE_NUMBER_REGEX.test(form.secondaryMobile.trim())) {
        errors['Second Mobile'] = 'Mobile Number Must Be 10 Digits Only.';
      } else if (form.secondaryMobile.trim() === form.mobile?.trim()) {
        errors['Second Mobile'] = 'Secondary Mobile Number is same as the Mobile Number.';
      }
    }

    // Experience
    if (
      form.yearsOfExperience === null ||
      form.yearsOfExperience === undefined ||
      form.yearsOfExperience === ''
    ) {
      errors['Experience'] = 'Exp. in Years is Required.';
    } else if (isNaN(Number(form.yearsOfExperience))) {
      errors['Experience'] = 'Only numbers are allowed.';
    } else if (Number(form.yearsOfExperience) < 0) {
      errors['Experience'] = 'Minimum 0 Year.';
    } else if (Number(form.yearsOfExperience) > 100) {
      errors['Experience'] = 'Maximum 100 Years.';
    } else if (
      form.monthsOfExperience === null ||
      form.monthsOfExperience === undefined ||
      form.monthsOfExperience === ''
    ) {
      errors['Experience'] = 'Exp. in Months is required.';
    } else if (isNaN(Number(form.monthsOfExperience))) {
      errors['Experience'] = 'Only numbers are allowed.';
    } else if (Number(form.monthsOfExperience) < 0) {
      errors['Experience'] = 'Minimum 0 Month.';
    } else if (Number(form.monthsOfExperience) > 11) {
      errors['Experience'] = 'Maximum 11 Months.';
    }

    // Technology
    if (!form.technology || form.technology.length === 0) {
      errors['Technology'] = 'Technology is required.';
    }

    // Current Company
    if (!form.currentCompany?.trim()) {
      errors['Current Company'] = 'Current Company is required.';
    }

    // Current CTC
    if (form.currentCTC === null || form.currentCTC === undefined || form.currentCTC === '') {
      errors['Current CTC'] = 'Current CTC is required.';
    } else if (isNaN(Number(form.currentCTC))) {
      errors['Current CTC'] = 'Only positive numbers are allowed.';
    } else if (Number(form.currentCTC) < 0) {
      errors['Current CTC'] = 'CTC should not be less then 0.';
    } else if (Number(form.currentCTC) > 99) {
      errors['Current CTC'] = 'CTC should not be more then 99.';
    }

    // Location
    if (!form.candidateLocation) {
      errors['Candidate Location'] = 'Candidate location is required.';
      errors['Location'] = 'Candidate location is required.';
    } else if (form.candidateLocation === 'other' && !form.location?.trim()) {
      errors['Candidate Location'] = 'Candidate Location is required.';
      errors['Location'] = 'Candidate Location is required.';
    }

    // Source
    if (!form.candidateSource) {
      errors['Candidate Source'] = 'Candidate source is required.';
    } else if (form.candidateSource === 4 && !form.referenceEmployeeName?.trim()) {
      errors['Candidate Source'] = 'Employee Ref Name is required.';
      errors['Employee Ref Name'] = 'Employee Ref Name is required.';
    }

    return errors;
  };

  const handleSaveAllFields = async () => {
    if (!candidateId) return;

    const errors = validateAllFields(inlineForm);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSavingForm(true);
    setFieldErrors({});

    try {
      const finalLocation =
        inlineForm.candidateLocation === 'other'
          ? inlineForm.location?.trim()
          : inlineForm.candidateLocation?.trim();

      let postData = new FormData();
      postData.append('firstName', inlineForm.firstName?.trim() || '');
      postData.append('lastName', inlineForm.lastName?.trim() || '');
      postData.append('gender', inlineForm.gender);
      postData.append('yearOfExperience', inlineForm.yearsOfExperience ?? 0);
      postData.append('monthsOfExperience', inlineForm.monthsOfExperience ?? 0);
      postData.append('email', inlineForm.email?.trim() || '');
      postData.append('mobile', inlineForm.mobile?.trim() || '');
      postData.append(
        'secondaryMobile',
        inlineForm.secondaryMobile ? inlineForm.secondaryMobile.trim() : ''
      );
      postData.append('currentCompany', inlineForm.currentCompany?.trim() || '');

      if (inlineForm.technology && inlineForm.technology.length > 0) {
        postData.append('candidateTechnology', inlineForm.technology);
      }

      if (
        inlineForm.currentCTC !== null &&
        inlineForm.currentCTC !== undefined &&
        inlineForm.currentCTC !== ''
      ) {
        postData.append('currentCTC', inlineForm.currentCTC);
      }

      if (finalLocation) {
        postData.append('candidateLocation', finalLocation);
      }

      postData.append('candidateSource', inlineForm.candidateSource || 1);

      if (inlineForm.candidateSource === 4 && inlineForm.referenceEmployeeName?.trim()) {
        postData.append('referenceEmployeeName', inlineForm.referenceEmployeeName.trim());
      } else {
        postData.append('referenceEmployeeName', '');
      }

      dispatch(
        editCandidate({
          postData,
          CandidateId: candidateId
        })
      );

      setIsEditing(false);
      setTimeout(() => {
        if (onStatusUpdate) {
          onStatusUpdate();
        }
        dispatch(candidateDetailsAction(candidateId));
      }, 500);
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to update details';
      toast.error(msg);
    } finally {
      setSavingForm(false);
    }
  };

  const resetAddForm = () => {
    setNewCallStatus(null);
    setNewScreeningNote('');
    setShowAddForm(false);
  };

  const handleSaveStatus = () => {
    if (!newCallStatus || !candidateId) return;
    setSavingStatus(true);

    dispatch(
      addCandidateCallStatusAction({
        CandidateId: candidateId,
        postData: { callStatus: newCallStatus, screeningCall: newScreeningNote.trim() }
      })
    );

    setTimeout(() => {
      if (onStatusUpdate) onStatusUpdate();
      else dispatch(candidateDetailsAction(candidateId));
      resetAddForm();
      setSavingStatus(false);
    }, 600);
  };

  const handleEditNote = (history, idx) => {
    setEditingHistoryIdx(idx);
    setEditingNoteValue(history.ScreeningCall || '');
  };

  const handleSaveEditedNote = (history) => {
    if (!candidateId) return;
    setSavingNoteStatus(true);
    dispatch(
      editCandidateScreeningNoteAction({
        HistoryId: history.ID,
        postData: { screeningCall: editingNoteValue.trim() }
      })
    );

    setTimeout(() => {
      if (onStatusUpdate) onStatusUpdate();
      else dispatch(candidateDetailsAction(candidateId));
      setEditingHistoryIdx(null);
      setEditingNoteValue('');
      setSavingNoteStatus(false);
    }, 600);
  };

  const timelineMarker = (item) => {
    return <span className={`ud-timeline-marker ud-status-${item.CallStatus || 10}`} />;
  };

  const timelineContent = (item, idx) => {
    const isOwner = !isReadOnly && item.UpdatedByUser?.ID === auth?.userId;
    const isNoteEditing = editingHistoryIdx === idx;

    return (
      <div className={`ud-timeline-card ud-status-${item.CallStatus || 10}`}>
        <div className="ud-timeline-header">
          <span className={`ud-status-pill ud-status-${item.CallStatus || 10}`}>
            {CALLSTATUS_MAP[item.CallStatus] || 'Unknown'}
          </span>
          <span className="ud-timeline-date">
            <i className="pi pi-calendar mr-1" />
            {moment(item.DateCreated).format('DD MMM YYYY, h:mm A')}
          </span>
        </div>
        {isNoteEditing ? (
          <div className="ud-edit-note-wrap">
            <TextareaAutosize
              value={editingNoteValue}
              onChange={(e) => setEditingNoteValue(e.target.value)}
              minRows={3}
              className="p-inputtextarea p-inputtext p-component ud-edit-note-input w-full"
              placeholder="Edit screening note…"
              maxLength={5000}
            />
            <div className="form-btn-wrapper justify-flex-end ud-edit-note-actions">
              <CustomButton
                variant="contained"
                size="small"
                onClick={() => setEditingHistoryIdx(null)}
                className="gray-btn">
                {BUTTON_TYPES.CANCEL}
              </CustomButton>
              <CustomButton
                type="button"
                size="small"
                variant="contained"
                disabled={savingNoteStatus}
                onClick={() => handleSaveEditedNote(item)}>
                {savingNoteStatus ? 'Saving...' : BUTTON_TYPES.SAVE}
              </CustomButton>
            </div>
          </div>
        ) : (
          item.ScreeningCall && (
            <div className="ud-screening-note">
              <i className="pi pi-comment ud-note-icon" />
              <span className="ud-screening-note-text">{item.ScreeningCall}</span>
              {isOwner && (
                <IconButton
                  className="ud-note-edit-btn"
                  title="Edit your note"
                  size="small"
                  onClick={() => handleEditNote(item, idx)}>
                  <i className="pi pi-pencil" />
                </IconButton>
              )}
            </div>
          )
        )}
        <div className="ud-timeline-footer">
          <span className="ud-updated-by-label">Updated by</span>
          {renderHrAvatar(
            item.UpdatedByUser?.ID,
            item.UpdatedByUser?.UserProfile,
            item.UpdatedByUser?.FirstName,
            item.UpdatedByUser?.LastName,
            true
          )}
        </div>
      </div>
    );
  };

  const headerContent = (
    <div className="ud-dialog-header">
      {(showHistory || showAddForm) && (
        <IconButton
          className="ud-back-btn"
          onClick={() => {
            setShowHistory(false);
            setShowAddForm(false);
            setEditingHistoryIdx(null);
            setIsEditing(false);
          }}
          title="Back"
          size="small">
          <i className="pi pi-arrow-left" />
        </IconButton>
      )}
      <div className="ud-header-content">
        {!showHistory && !showAddForm && (
          <div className="ud-header-icon">
            <i className="pi pi-user" />
          </div>
        )}
        <span className="ud-header-title">
          {showAddForm
            ? 'Add New Call Status'
            : showHistory
            ? `${LABEL_TYPES.CALL_STATUS_HISTORY} - ${
                data
                  ?.find((d) => d.title === 'Name')
                  ?.value?.replace(/\n/g, '')
                  ?.replace(/\s+/g, ' ')
                  ?.trim() || ''
              }`
            : header}
        </span>
      </div>
    </div>
  );

  const renderHeaderIcons = () => {
    if (isReadOnly || !candidateId || showHistory || showAddForm) return null;

    return (
      <IconButton
        className={`ud-header-edit-btn ${isEditing ? 'is-editing' : ''}`}
        onClick={handleToggleEditMode}
        title={isEditing ? 'Cancel Edit' : 'Edit Details'}
        size="small">
        <i className="pi pi-pencil" />
      </IconButton>
    );
  };

  const footerContent = showAddForm ? null : isEditing ? (
    <div className="ud-dialog-footer">
      <div className="form-btn-wrapper justify-flex-end">
        <CustomButton
          variant="contained"
          onClick={handleCancelEdit}
          color="error"
          className="gray-btn"
          disabled={savingForm}>
          {BUTTON_TYPES.CANCEL}
        </CustomButton>
        <CustomButton
          type="button"
          variant="contained"
          disabled={savingForm}
          onClick={handleSaveAllFields}>
          {savingForm ? 'Saving...' : BUTTON_TYPES.SAVE}
        </CustomButton>
      </div>
    </div>
  ) : (
    <div className="ud-dialog-footer">
      <div className="ud-footer-actions flex align-items-center">
        {!showHistory && historyList?.length > 0 ? (
          <CustomButton
            variant="outlined"
            size="small"
            className="ud-history-btn"
            onClick={() => setShowHistory(true)}>
            {LABEL_TYPES.CALL_STATUS_HISTORY}
          </CustomButton>
        ) : (
          <div />
        )}
        <div className="flex-1" />
        {candidateId && !showHistory && !isReadOnly && (
          <CustomButton
            variant="contained"
            size="small"
            className="ud-add-status-btn"
            onClick={() => setShowAddForm(true)}>
            <i className="pi pi-plus" />
            Add Note
          </CustomButton>
        )}
      </div>
    </div>
  );

  return (
    <>
      <ReactTooltip />
      <Dialog
        header={headerContent}
        footer={footerContent}
        icons={renderHeaderIcons}
        className="ud-candidate-dialog"
        visible={show}
        onHide={() => {
          onHide();
          setShowHistory(false);
          resetAddForm();
          setEditingHistoryIdx(null);
          setIsEditing(false);
          setFieldErrors({});
        }}>
        <div className="ud-dialog-body">
          {showAddForm ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (newCallStatus && !savingStatus) {
                  handleSaveStatus();
                }
              }}
              className="ud-add-status-content">
              <div className="ud-form-row">
                <Dropdown
                  value={newCallStatus}
                  options={CALL_STATUS_OPTIONS}
                  onChange={(e) => setNewCallStatus(e.value)}
                  optionLabel="name"
                  optionValue="code"
                  placeholder="Select Call Status *"
                  className="ud-status-dropdown w-full"
                  panelClassName="custom-dropdown-panel"
                />
              </div>
              <div className="ud-form-row ud-form-row-note">
                <TextareaAutosize
                  value={newScreeningNote}
                  onChange={(e) => setNewScreeningNote(e.target.value)}
                  placeholder="Screening note (optional)"
                  minRows={3}
                  className="p-inputtextarea p-inputtext p-component ud-note-textarea w-full"
                  maxLength={5000}
                />
              </div>
              <div className="form-btn-wrapper justify-flex-end ud-add-status-actions">
                <CustomButton
                  variant="contained"
                  onClick={resetAddForm}
                  color="error"
                  className="gray-btn">
                  {BUTTON_TYPES.CANCEL}
                </CustomButton>
                <CustomButton
                  type="submit"
                  variant="contained"
                  disabled={!newCallStatus || savingStatus}>
                  {BUTTON_TYPES.SAVE}
                </CustomButton>
              </div>
            </form>
          ) : !showHistory ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (isEditing && !savingForm) {
                  handleSaveAllFields();
                }
              }}>
              <DetailCard
                items={data}
                isEditing={isEditing}
                inlineForm={inlineForm}
                setInlineForm={setInlineForm}
                fieldErrors={fieldErrors}
                interviewTechnologyType={interviewTechnologyType}
              />
              {isEditing && <button type="submit" className="hidden-submit-btn" />}
            </form>
          ) : (
            <div className="ud-timeline-wrap">
              {historyList?.length > 0 ? (
                <Timeline
                  value={historyList}
                  marker={timelineMarker}
                  content={(item, idx) => timelineContent(item, idx)}
                  className="ud-prime-timeline"
                  align="left"
                />
              ) : (
                <div className="ud-empty-history">
                  <i className="pi pi-inbox ud-empty-icon" />
                  <p>No call history available</p>
                </div>
              )}
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default UserDetails;
